import React, { useEffect, useState } from 'react'
import {
  Image,
  ImageStyle,
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import pluralize from 'pluralize'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@components/base'
import { useGoBack } from '@src/utils'
import { OnboardingStackParamsList } from '@src/navigation/types'
import { freeTrialOfferImage } from '@src/assets/images'
import { BillingProductCategory, Product } from '@src/types'
import { productsByCategorySelector } from '@src/selectors/app'
import { LoadingContainer } from '@src/screens/Common/containers/LoadingContainer'
import { CheckoutKind } from '@src/screens/Marketplace/types/types'
import { useCanUpgradeToProduct } from '@src/screens/Marketplace/utils/hooks'
import { BenefitCard } from '@src/screens/Marketplace/components/NewMarketplace/ProductExplanation/BenefitCard'
import {
  OWN_SENSOR_BENEFITS,
  VideoCallProductTypes,
} from '@src/screens/Marketplace/components/NewMarketplace/definitions'
import { ProductOption } from './FreeTrialSubscription/ProductOption'
import { TrialStep } from './FreeTrialSubscription/TrialStep'

const PRODUCT_CATEGORY = BillingProductCategory.FreeTrial
const RUNNER_IMAGE_ASPECT_RATIO = 180 / 392

const BENEFITS = OWN_SENSOR_BENEFITS[VideoCallProductTypes.Paid]

const getBenefitByDescription = (description: string) => {
  if (description.includes('sensor')) {
    return BENEFITS.find((benefit) => benefit.title.toLowerCase().includes('sensor')) || BENEFITS[0]
  }

  if (description.includes('glucose')) {
    return (
      BENEFITS.find((benefit) => benefit.title.toLowerCase().includes('glucose')) || BENEFITS[1]
    )
  }

  if (description.includes('nutritionist')) {
    return (
      BENEFITS.find((benefit) => benefit.title.toLowerCase().includes('nutritionist')) ||
      BENEFITS[2]
    )
  }

  return null
}

export const FreeTrialSubscriptionOffer = () => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation()
  const onboardingNavigation = useNavigation<StackNavigationProp<OnboardingStackParamsList>>()
  const goBack = useGoBack()
  const dispatch = useDispatch()
  const route = useRoute<RouteProp<OnboardingStackParamsList, 'FreeTrialSubscriptionOffer'>>()
  const { product, subscription } = route.params

  const freeTrialProducts = useSelector(productsByCategorySelector(PRODUCT_CATEGORY))
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(product)
  const [productsLoaded, setProductsLoaded] = useState(false)

  const interval = selectedProduct?.interval.key || product.interval.key
  const trialDays = selectedProduct?.trialDays || product.trialDays

  const eligibleForUpgrade = useCanUpgradeToProduct(subscription, selectedProduct?.id)

  const dimensions = useWindowDimensions()
  const imageHeight = RUNNER_IMAGE_ASPECT_RATIO * dimensions.width

  useEffect(() => {
    if (!product.trialDays) {
      goBack()
    }
  }, [goBack, product.trialDays])

  useEffect(() => {
    dispatch({
      type: 'app/fetchProducts',
      payload: { category: PRODUCT_CATEGORY },
      complete: () => {
        setProductsLoaded(true)
      },
    })
  }, [dispatch])

  if (!trialDays) {
    return null
  }

  const onSubscribePress = () => {
    if (subscription && eligibleForUpgrade) {
      return navigation.navigate('SubscriptionCheckout', {
        subscriptionId: subscription.id,
        product: selectedProduct || product,
        checkoutKind: CheckoutKind.UpgradeProduct,
      })
    }

    navigation.navigate('SubscriptionCheckout', { product: selectedProduct || product })
  }

  const onPlanSelect = (product: Product) => {
    setSelectedProduct(product)
  }

  const productBenefits =
    selectedProduct?.includedOptions?.flatMap((description) => {
      const benefit = getBenefitByDescription(description)
      if (!benefit) {
        return []
      }
      return { ...benefit, description }
    }) || BENEFITS

  const onBackPress = () => {
    if (navigation.canGoBack()) {
      return goBack()
    }

    onboardingNavigation.replace('OnboardingSubscriptionOptions')
  }

  return (
    <SafeAreaView edges={['top', 'bottom']} style={styles.container}>
      <TouchableOpacity
        onPress={onBackPress}
        activeOpacity={0.7}
        accessibilityLabel="Go Back"
        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
      >
        <Icon name="caret-left" size="20" weight="bold" style={styles.defaultLeftIcon} />
      </TouchableOpacity>
      <ScrollView>
        <View style={styles.header}>
          <Image
            source={freeTrialOfferImage.imageSource}
            resizeMode="contain"
            style={{ height: imageHeight } as ImageStyle}
          />
          <View style={styles.textOverlayContainer}>
            <Text style={styles.title} type="title-1">
              First {pluralize('day', trialDays, true)} on us
            </Text>
          </View>
        </View>
        <View style={styles.contentContainer}>
          <View style={styles.trialSteps}>
            <Text style={styles.description} type="title-3">
              How your free trial works
            </Text>
            <TrialStep
              title="Today"
              description="Connect your sensor and activate it in our app."
              iconName="lock-simple-open"
              lightConnector
              darkConnector
            />
            <TrialStep
              title={`In ${pluralize('day', trialDays - 1, true)}`}
              description="We will check-in how you’re doing! And a quick reminder about your trial ending."
              iconName="bell"
              iconContainerStyle={{ transform: [{ rotate: '-15deg' }] }}
              lightConnector
            />
            <TrialStep
              title={`In ${pluralize('day', trialDays, true)}`}
              description={`Your ${interval} subscription will start on ${
                ' ' + moment().add(trialDays, 'days').format('MM/DD/YYYY')
              }`}
              iconName="star"
            />
          </View>
          <View style={styles.planSelection}>
            <Text type="regular">Select a plan</Text>
            <View style={styles.plans}>
              {!productsLoaded && <LoadingContainer loadingMessage="Loading products" />}
              {productsLoaded &&
                freeTrialProducts
                  .sort((a, b) => a.price - b.price) // put cheaper unit prices first
                  .map((product) => (
                    <ProductOption
                      onPress={() => onPlanSelect(product)}
                      selected={selectedProduct?.key === product.key}
                      key={product.key}
                      interval={product.interval.key}
                      pricePerMonth={product.price / (product.interval.key === 'yearly' ? 12 : 1)}
                      tag={product.interval.key === 'yearly' ? 'MOST POPULAR' : undefined}
                      breakdownText={
                        product.interval.key === 'yearly'
                          ? `at $${product.price}`
                          : `$${product.price * 12}/year`
                      }
                    />
                  ))}
            </View>
          </View>
          <View style={styles.benefitsHeader}>
            <Text type="regular">What's included?</Text>
          </View>
          {productBenefits.map((benefit) => {
            return <BenefitCard key={benefit.title} benefits={productBenefits} benefit={benefit} />
          })}
        </View>
      </ScrollView>
      <View style={styles.footer}>
        <Button
          type="primary"
          size="block"
          accessibilityLabel="Start my free trial"
          onPress={onSubscribePress}
        >
          Start my free trial
        </Button>
        <Text style={styles.buttonDescription} type="regular">
          Cancel anytime before trial end. Afterwards, subscription is charged
          {' ' + interval}
        </Text>
      </View>
    </SafeAreaView>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    paddingBottom: 16,
    backgroundColor: 'theme.background',
  },
  defaultLeftIcon: {
    marginTop: 12,
    marginLeft: 16,
    marginBottom: 12,
  },
  header: {
    alignItems: 'center',
  },
  textOverlayContainer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    top: '40%',
    left: 0,
    right: 0,
    bottom: 0,
  },
  title: {
    color: 'theme.solid.white',
    marginTop: 24,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 4, // Android shadow
  },
  contentContainer: {
    paddingHorizontal: 16,
  },
  trialSteps: {
    alignItems: 'flex-start',
  },
  planSelection: {
    alignItems: 'center',
    marginVertical: 16,
  },
  plans: {
    width: '100%',
    flexBasis: '100%',
    alignContent: 'stretch',
    justifyContent: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginVertical: 16,
    gap: 8,
  },
  benefitsHeader: {
    alignItems: 'center',
  },
  description: {
    alignSelf: 'center',
    marginVertical: 16,
    maxWidth: 300,
  },
  footer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  buttonDescription: {
    marginTop: 16,
    color: 'theme.text.secondary',
    textAlign: 'center',
  },
})
